import { ReactElement } from 'react';
import { FloatingDelayGroup } from '@floating-ui/react';
import { useTooltip } from '../../hooks/useTooltip';
import Tooltip from './Tooltip';

export interface TooltipRenderProps
  extends ReturnType<ReturnType<typeof useTooltip>['getReferenceProps']> {
  onClick?: (event: React.MouseEvent) => void;
}

interface TooltipWrapperProps {
  children: (props: TooltipRenderProps) => ReactElement;
  content: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  onClick?: (event: React.MouseEvent) => void;
  externalRef?:
    | React.RefObject<HTMLElement>
    | ((node: HTMLElement | null) => void);
  followCursor?: boolean;
}

export default function TooltipWrapper(props: TooltipWrapperProps) {
  return (
    <FloatingDelayGroup delay={{ open: 500, close: 100 }}>
      <TooltipContent {...props} />
    </FloatingDelayGroup>
  );
}

function TooltipContent({
  children,
  content,
  placement,
  onClick,
  externalRef,
  followCursor,
}: TooltipWrapperProps) {
  const tooltip = useTooltip({
    content,
    placement,
    followCursor,
  });

  const handleClick = (event: React.MouseEvent) => {
    tooltip.setIsOpen(false);

    if (onClick) {
      onClick(event);
    }
  };

  const setMultipleRefs = (node: HTMLElement | null) => {
    tooltip.refs.setReference(node);

    if (externalRef) {
      if (typeof externalRef === 'function') {
        externalRef(node);
      } else if ('current' in externalRef) {
        (externalRef as React.MutableRefObject<HTMLElement | null>).current =
          node;
      }
    }
  };

  const renderProps: TooltipRenderProps = {
    ...tooltip.getReferenceProps(),
    ref: setMultipleRefs,
    onClick: handleClick,
  };

  const childElement = children(renderProps);

  return (
    <>
      {childElement}
      <Tooltip
        isOpen={tooltip.isOpen}
        content={tooltip.content}
        tooltipId={tooltip.tooltipId}
        refs={tooltip.refs}
        floatingStyles={tooltip.floatingStyles}
        getFloatingProps={tooltip.getFloatingProps}
      />
    </>
  );
}
